import { useEffect, useState } from 'react'
import { loadRevealSeen, saveRevealSeen } from '../storage'

// Exact scores are session-scoped rather than persisted (ticket 0418): the
// detonation plays again on a fresh app open, but not when you tab away and
// back inside one session. Module-level, so it survives a remount and dies with
// the page. Keyed by gameweek, since the persisted set is too.
const sessionSeen = new Map<number, Set<string>>()

/** Test seam: the session cache outlives a remount by design, which also means
 * it outlives a test. */
export function resetSessionRevealSeen(): void {
  sessionSeen.clear()
}

export interface RevealSeenController {
  /**
   * GameIds whose full-time reveal has already played, or `null` while the set for the current
   * gameweek is still resolving. GameweekGrid treats null as "not ready" and holds settled
   * fixtures at rest rather than risk replaying a once-ever Phase A against a stale set.
   */
  revealSeenGameIds: Set<string> | null
  /** Record fixtures as revealed so navigating away and back does not replay
   * them. `persist: false` keeps them for this session only. */
  markSeen: (gameIds: string[], persist?: boolean) => void
}

/**
 * The per-gameweek "this reveal has already played for me" set.
 *
 * Reloaded whenever the gameweek changes — cheaper and more robust than threading a reload through
 * every gameweek-change call site. The stored value carries the gameweek it was loaded for, so a
 * set belonging to the previous gameweek reads as `null` (not ready) instead of being mistaken for
 * the current one; that scoping is the whole reason this is a hook rather than a bare `Set`.
 */
export function useRevealSeen(currentGameweek: number, isLoading: boolean): RevealSeenController {
  const [seen, setSeen] = useState<{ gameweek: number; ids: Set<string> } | null>(null)

  useEffect(() => {
    if (isLoading) return
    let cancelled = false
    const gameweek = currentGameweek
    loadRevealSeen(gameweek)
      .then((ids) => {
        if (!cancelled) setSeen({ gameweek, ids: new Set(ids) })
      })
      .catch(() => {
        // Treat a failed load as "nothing seen yet" for this gameweek rather than blocking the
        // screen — worst case a reveal replays, which the per-fixture mark below then corrects.
        if (!cancelled) setSeen({ gameweek, ids: new Set() })
      })
    return () => {
      cancelled = true
    }
  }, [currentGameweek, isLoading])

  const loaded = seen && seen.gameweek === currentGameweek ? seen.ids : null

  return {
    // The two sets answer the same question — "has this already played for me?"
    // — so the grid sees one set and never has to know which is which.
    revealSeenGameIds:
      loaded === null ? null : new Set([...loaded, ...(sessionSeen.get(currentGameweek) ?? [])]),

    markSeen: (gameIds, persist = true) => {
      if (gameIds.length === 0) return
      const gameweek = currentGameweek
      if (!persist) {
        const forWeek = sessionSeen.get(gameweek) ?? new Set<string>()
        for (const id of gameIds) forWeek.add(id)
        sessionSeen.set(gameweek, forWeek)
        // Nudge state so the grid re-reads the merged set.
        setSeen((prev) => (prev && prev.gameweek === gameweek ? { ...prev } : prev))
        return
      }
      // Optimistic: the reveal has already played on screen, so the set updates immediately and
      // the additive server write is fire-and-forget.
      setSeen((prev) => {
        const base = prev && prev.gameweek === gameweek ? prev.ids : new Set<string>()
        const next = new Set(base)
        for (const id of gameIds) next.add(id)
        return { gameweek, ids: next }
      })
      saveRevealSeen(gameweek, gameIds).catch(() => {
        // Non-fatal — on the next load an un-persisted fixture simply replays, the same benign
        // outcome as a failed initial load.
      })
    },
  }
}
